import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Plus } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { athleteAPI } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';
import Modal from './Modal';
import { Select, TextInput } from './ui';
import ErrorState from './ErrorState';
import {
  rangeError,
  hasNoErrors,
  RANGE_SPRINT_TIME_SEC,
  RANGE_POP_TIME_SEC,
  RANGE_VELOCITY_MPH,
  RANGE_SPIN_RATE_RPM,
  RANGE_GRIP_STRENGTH_LBS,
} from '../utils/validation';

// Pitcher positions to detect
const PITCHER_POSITIONS = ['Pitcher', 'Relief Pitcher', 'Starting Pitcher', 'Starter/Reliever'];

interface PitchEntry {
  id: string;
  pitch_type: string;
  velocity: string;
  spin_rate: string;
}

interface PerformanceMetricsEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData: {
    id: string;
    stats?: {
      home_to_1st?: number;
      sixty_yard_time?: number;
      forty_yard_time?: number;
      twenty_yard_time?: number;
      ten_yard_time?: number;
      pop_time?: number;
      grip_strength?: number;
      exit_velocity?: number;
      overhand_velocity?: number;
      pitch_velocities?: Array<{ pitch_type: string; value: number }>;
      spin_rates?: Array<{ pitch_type: string; value: number }>;
    };
  };
  onUpdate: (updatedData: any) => void;
  /** Render inside the unified ProfileEditModal (no own overlay). */
  embedded?: boolean;
  /** Called after a successful save. Falls back to onClose. */
  onSaved?: () => void;
}

const PerformanceMetricsEditModal: React.FC<PerformanceMetricsEditModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onUpdate,
  embedded = false,
  onSaved
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [isSaving, setIsSaving] = useState(false);
  // #163 — track whether the on-open fetch succeeded. If it failed we must NOT
  // let Save fire, because handleSave sends the pitch arrays (even empty) which
  // would DELETE every stored pitch velocity / spin rate.
  const [loadFailed, setLoadFailed] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Form state
  const [homeToFirst, setHomeToFirst] = useState('');
  const [sixtyYardTime, setSixtyYardTime] = useState('');
  const [fortyYardTime, setFortyYardTime] = useState('');
  const [twentyYardTime, setTwentyYardTime] = useState('');
  const [tenYardTime, setTenYardTime] = useState('');
  const [popTime, setPopTime] = useState('');
  const [gripStrength, setGripStrength] = useState('');
  const [exitVelocity, setExitVelocity] = useState('');
  const [overhandVelocity, setOverhandVelocity] = useState('');

  // Pitcher metrics state
  const [athletePositions, setAthletePositions] = useState<string[]>([]);
  const [availablePitchTypes, setAvailablePitchTypes] = useState<string[]>([]);
  const [pitchEntries, setPitchEntries] = useState<PitchEntry[]>([]);

  // Check if athlete is a pitcher
  const isPitcher = useMemo(() => {
    return athletePositions.some(pos => PITCHER_POSITIONS.includes(pos));
  }, [athletePositions]);

  // Fetch performance metrics and pitcher data when modal opens
  const fetchAllData = useCallback(async () => {
      if (!sessionToken || !initialData?.id) return;
      setLoadFailed(false);
      
      try {
        // Fetch performance metrics directly from API to get pitch data
        const metricsResponse = await athleteAPI.getPerformanceMetrics(sessionToken, initialData.id);
        
        if (metricsResponse.success && metricsResponse.data?.metrics) {
          const metrics = metricsResponse.data.metrics;
          setHomeToFirst(metrics.home_to_first?.toString() || '');
          setSixtyYardTime(metrics.sixty_yard_time?.toString() || '');
          setFortyYardTime(metrics.forty_yard_time?.toString() || '');
          setTwentyYardTime(metrics.twenty_yard_time?.toString() || '');
          setTenYardTime(metrics.ten_yard_time?.toString() || '');
          setPopTime(metrics.pop_time?.toString() || '');
          setGripStrength(metrics.grip_strength?.toString() || '');
          setExitVelocity(metrics.exit_velocity?.toString() || '');
          setOverhandVelocity(metrics.overhand_velocity?.toString() || '');

          // Initialize pitch entries from API data
          const velocities = metrics.pitch_velocities || [];
          const spinRates = metrics.spin_rates || [];
          const pitchTypeSet = new Set([
            ...velocities.map(v => v.pitch_type),
            ...spinRates.map(s => s.pitch_type)
          ]);
          
          const entries: PitchEntry[] = Array.from(pitchTypeSet).map(pitchType => ({
            id: `existing-${pitchType}`,
            pitch_type: pitchType,
            velocity: velocities.find(v => v.pitch_type === pitchType)?.value?.toString() || '',
            spin_rate: spinRates.find(s => s.pitch_type === pitchType)?.value?.toString() || ''
          }));
          
          setPitchEntries(entries.length > 0 ? entries : []);
        } else {
          // Fall back to initialData.stats if API returns no data
          if (initialData?.stats) {
            setHomeToFirst(initialData.stats.home_to_1st?.toString() || '');
            setSixtyYardTime(initialData.stats.sixty_yard_time?.toString() || '');
            setFortyYardTime(initialData.stats.forty_yard_time?.toString() || '');
            setTwentyYardTime(initialData.stats.twenty_yard_time?.toString() || '');
            setTenYardTime(initialData.stats.ten_yard_time?.toString() || '');
            setPopTime(initialData.stats.pop_time?.toString() || '');
            setGripStrength(initialData.stats.grip_strength?.toString() || '');
            setExitVelocity(initialData.stats.exit_velocity?.toString() || '');
            setOverhandVelocity(initialData.stats.overhand_velocity?.toString() || '');
          }
          setPitchEntries([]);
        }

        // Fetch sport info to get positions
        const sportInfoResponse = await athleteAPI.getSportInfo(sessionToken, initialData.id);
        if (sportInfoResponse.sportInfo?.positions) {
          setAthletePositions(sportInfoResponse.sportInfo.positions);
          
          // If athlete is a pitcher, fetch pitch types
          const positions = sportInfoResponse.sportInfo.positions;
          const hasPitcherPosition = positions.some((pos: string) => PITCHER_POSITIONS.includes(pos));
          
          if (hasPitcherPosition) {
            const pitchTypesResponse = await athleteAPI.getPitchTypes(sessionToken);
            if (pitchTypesResponse.success && pitchTypesResponse.pitchTypes) {
              setAvailablePitchTypes(pitchTypesResponse.pitchTypes);
            }
          }
        }
      } catch (error) {
        // #163 — surface the failure so Save is blocked instead of silently
        // deleting stored pitch data.
        setLoadFailed(true);
      }
  }, [sessionToken, initialData?.id]);

  useEffect(() => {
    if (!isOpen) return;
    setHasChanges(false);
    fetchAllData();
  }, [isOpen, fetchAllData]);

  // Pitch entry helpers
  const addPitchEntry = () => {
    setHasChanges(true);
    setPitchEntries(prev => [
      ...prev,
      { id: `pitch-${Date.now()}`, pitch_type: '', velocity: '', spin_rate: '' }
    ]);
  };

  const removePitchEntry = (id: string) => {
    setHasChanges(true);
    setPitchEntries(prev => prev.filter(entry => entry.id !== id));
  };

  const updatePitchEntry = (id: string, field: keyof PitchEntry, value: string) => {
    setHasChanges(true);
    setPitchEntries(prev => prev.map(entry =>
      entry.id === id ? { ...entry, [field]: value } : entry
    ));
  };

  // #189 — sanity-range validation for every numeric field. Empty is OK
  // (means "cleared"); a present out-of-range value blocks Save.
  const fieldErrors = useMemo(() => ({
    homeToFirst: rangeError(homeToFirst, RANGE_SPRINT_TIME_SEC, { unit: 'sec' }),
    sixtyYardTime: rangeError(sixtyYardTime, RANGE_SPRINT_TIME_SEC, { unit: 'sec' }),
    fortyYardTime: rangeError(fortyYardTime, RANGE_SPRINT_TIME_SEC, { unit: 'sec' }),
    twentyYardTime: rangeError(twentyYardTime, RANGE_SPRINT_TIME_SEC, { unit: 'sec' }),
    tenYardTime: rangeError(tenYardTime, RANGE_SPRINT_TIME_SEC, { unit: 'sec' }),
    popTime: rangeError(popTime, RANGE_POP_TIME_SEC, { unit: 'sec' }),
    gripStrength: rangeError(gripStrength, RANGE_GRIP_STRENGTH_LBS, { unit: 'lbs' }),
    exitVelocity: rangeError(exitVelocity, RANGE_VELOCITY_MPH, { unit: 'mph' }),
    overhandVelocity: rangeError(overhandVelocity, RANGE_VELOCITY_MPH, { unit: 'mph' }),
  }), [homeToFirst, sixtyYardTime, fortyYardTime, twentyYardTime, tenYardTime, popTime, gripStrength, exitVelocity, overhandVelocity]);

  const pitchErrors = useMemo(
    () =>
      pitchEntries.map((e) => ({
        velocity: rangeError(e.velocity, RANGE_VELOCITY_MPH, { unit: 'mph' }),
        spin_rate: rangeError(e.spin_rate, RANGE_SPIN_RATE_RPM, { unit: 'rpm' }),
      })),
    [pitchEntries],
  );

  const hasErrors =
    !hasNoErrors(fieldErrors) || pitchErrors.some((pe) => Boolean(pe.velocity) || Boolean(pe.spin_rate));

  const errorTextStyle: React.CSSProperties = { display: 'block', marginTop: '6px', fontSize: '13px', color: '#DC2626' };

  const handleSave = async () => {
    if (!sessionToken || !initialData?.id) return;

    // #163 — never save on top of a failed load: the payload's (possibly empty)
    // pitch arrays would wipe the athlete's stored velocities/spin rates.
    if (loadFailed) {
      toast.error('Metrics could not be loaded. Retry before saving so your existing data is not overwritten.');
      return;
    }

    if (hasErrors) {
      toast.error('Please fix the highlighted fields before saving.');
      return;
    }

    setIsSaving(true);

    try {
      // Build pitch velocities and spin rates arrays
      const pitch_velocities = pitchEntries
        .filter(e => e.pitch_type && e.velocity)
        .map(e => ({ pitch_type: e.pitch_type, value: parseFloat(e.velocity) }));
      
      const spin_rates = pitchEntries
        .filter(e => e.pitch_type && e.spin_rate)
        .map(e => ({ pitch_type: e.pitch_type, value: parseFloat(e.spin_rate) }));

      const payload = {
        home_to_first: homeToFirst || undefined,
        sixty_yard_time: sixtyYardTime || undefined,
        forty_yard_time: fortyYardTime || undefined,
        twenty_yard_time: twentyYardTime || undefined,
        ten_yard_time: tenYardTime || undefined,
        pop_time: popTime || undefined,
        grip_strength: gripStrength || undefined,
        exit_velocity: exitVelocity || undefined,
        overhand_velocity: overhandVelocity || undefined,
        // Always send arrays (even empty) to properly delete removed entries
        pitch_velocities: pitch_velocities,
        spin_rates: spin_rates,
        recorded_date: new Date().toISOString().split('T')[0],
        measurement_context: 'profile_update',
      };

      const response = await athleteAPI.createOrUpdatePerformanceMetrics(
        sessionToken,
        initialData.id,
        payload
      );

      if (response.success) {
        toast.success('Performance metrics saved.');
        
        // Notify parent component of the update
        onUpdate({
          stats: {
            home_to_1st: homeToFirst || undefined,
            sixty_yard_time: sixtyYardTime || undefined,
            forty_yard_time: fortyYardTime || undefined,
            twenty_yard_time: twentyYardTime || undefined,
            ten_yard_time: tenYardTime || undefined,
            pop_time: popTime || undefined,
            grip_strength: gripStrength || undefined,
            exit_velocity: exitVelocity || undefined,
            overhand_velocity: overhandVelocity || undefined,
            pitch_velocities: pitch_velocities,
            spin_rates: spin_rates,
          }
        });

        (onSaved || onClose)();
      } else {
        toast.error('Failed to update performance metrics. Please try again.');
      }
    } catch (error) {
      console.error('Error updating performance metrics:', error);
      toast.error('Failed to update performance metrics. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      inline={embedded}
      isDirty={hasChanges}
      labelledById="perf-metrics-title"
      contentStyle={{
        background: 'white',
        borderRadius: '12px',
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '600px',
        width: isMobile ? 'calc(100vw - 32px)' : '100%',
        maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        fontFamily: 'Manrope, sans-serif',
      }}
    >
        {/* Header */}
        <div
          style={{
            padding: isMobile ? '16px' : '24px 28px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2
            id="perf-metrics-title"
            style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1F2937',
              margin: 0,
            }}
          >
            Edit Performance Metrics
          </h2>
          <button
            onClick={handleCancel}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              color: '#6B7280',
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form - Scrollable */}
        <div style={{ padding: '28px', overflowY: 'auto', flex: 1 }}>
          {loadFailed ? (
            <ErrorState
              message="We couldn't load your performance metrics. Retry before editing so your saved data isn't overwritten."
              onRetry={fetchAllData}
            />
          ) : (
          <>
          {/* Speed & Agility Section */}
          <div style={{ marginBottom: '28px' }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '16px',
              paddingBottom: '8px',
              borderBottom: '2px solid #F9550D',
            }}>
              Speed & Agility
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
              {/* Home to 1st */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }} htmlFor="metrics-home-to-first">
                  Home to 1st (seconds)
                </label>
                <TextInput
                  id="metrics-home-to-first"
                  type="number"
                  step="0.01"
                  value={homeToFirst}
                  onChange={(e) => { setHomeToFirst(e.target.value); setHasChanges(true); }}
                  placeholder="4.23"
                  min={RANGE_SPRINT_TIME_SEC.min}
                  max={RANGE_SPRINT_TIME_SEC.max}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.homeToFirst && <span style={errorTextStyle}>{fieldErrors.homeToFirst}</span>}
              </div>

              {/* 5x10x5 Shuttle */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }} htmlFor="metrics-sixty-yard">
                  5x10x5 Shuttle (seconds)
                </label>
                <TextInput
                  id="metrics-sixty-yard"
                  type="number"
                  step="0.01"
                  value={sixtyYardTime}
                  onChange={(e) => { setSixtyYardTime(e.target.value); setHasChanges(true); }}
                  placeholder="7.25"
                  min={RANGE_SPRINT_TIME_SEC.min}
                  max={RANGE_SPRINT_TIME_SEC.max}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.sixtyYardTime && <span style={errorTextStyle}>{fieldErrors.sixtyYardTime}</span>}
              </div>

              {/* 40 Yard Time */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }} htmlFor="metrics-forty-yard">
                  40 Yard Time (seconds)
                </label>
                <TextInput
                  id="metrics-forty-yard"
                  type="number"
                  step="0.01"
                  value={fortyYardTime}
                  onChange={(e) => { setFortyYardTime(e.target.value); setHasChanges(true); }}
                  placeholder="4.85"
                  min={RANGE_SPRINT_TIME_SEC.min}
                  max={RANGE_SPRINT_TIME_SEC.max}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.fortyYardTime && <span style={errorTextStyle}>{fieldErrors.fortyYardTime}</span>}
              </div>

              {/* 20 Yard Time */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }} htmlFor="metrics-twenty-yard">
                  20 Yard Time (seconds)
                </label>
                <TextInput
                  id="metrics-twenty-yard"
                  type="number"
                  step="0.01"
                  value={twentyYardTime}
                  onChange={(e) => { setTwentyYardTime(e.target.value); setHasChanges(true); }}
                  placeholder="3.12"
                  min={RANGE_SPRINT_TIME_SEC.min}
                  max={RANGE_SPRINT_TIME_SEC.max}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.twentyYardTime && <span style={errorTextStyle}>{fieldErrors.twentyYardTime}</span>}
              </div>

              {/* 10 Yard Time */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }} htmlFor="metrics-ten-yard">
                  10 Yard Time (seconds)
                </label>
                <TextInput
                  id="metrics-ten-yard"
                  type="number"
                  step="0.01"
                  value={tenYardTime}
                  onChange={(e) => { setTenYardTime(e.target.value); setHasChanges(true); }}
                  placeholder="1.78"
                  min={RANGE_SPRINT_TIME_SEC.min}
                  max={RANGE_SPRINT_TIME_SEC.max}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.tenYardTime && <span style={errorTextStyle}>{fieldErrors.tenYardTime}</span>}
              </div>

              {/* Pop Time */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }} htmlFor="metrics-pop-time">
                  Pop Time (seconds)
                </label>
                <TextInput
                  id="metrics-pop-time"
                  type="number"
                  step="0.01"
                  value={popTime}
                  onChange={(e) => { setPopTime(e.target.value); setHasChanges(true); }}
                  placeholder="2.15"
                  min={RANGE_POP_TIME_SEC.min}
                  max={RANGE_POP_TIME_SEC.max}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.popTime && <span style={errorTextStyle}>{fieldErrors.popTime}</span>}
              </div>
            </div>
          </div>

          {/* Power & Strength Section */}
          <div>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '16px',
              paddingBottom: '8px',
              borderBottom: '2px solid #F9550D',
            }}>
              Power & Strength
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
              {/* Grip Strength */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }} htmlFor="metrics-grip-strength">
                  Grip Strength (lbs)
                </label>
                <TextInput
                  id="metrics-grip-strength"
                  type="number"
                  value={gripStrength}
                  onChange={(e) => { setGripStrength(e.target.value); setHasChanges(true); }}
                  placeholder="125"
                  min={RANGE_GRIP_STRENGTH_LBS.min}
                  max={RANGE_GRIP_STRENGTH_LBS.max}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.gripStrength && <span style={errorTextStyle}>{fieldErrors.gripStrength}</span>}
              </div>

              {/* Exit Velocity */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }} htmlFor="metrics-exit-velocity">
                  Exit Velocity (mph)
                </label>
                <TextInput
                  id="metrics-exit-velocity"
                  type="number"
                  value={exitVelocity}
                  onChange={(e) => { setExitVelocity(e.target.value); setHasChanges(true); }}
                  placeholder="92"
                  min={RANGE_VELOCITY_MPH.min}
                  max={RANGE_VELOCITY_MPH.max}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.exitVelocity && <span style={errorTextStyle}>{fieldErrors.exitVelocity}</span>}
              </div>

              {/* Overhand Velocity */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }} htmlFor="metrics-overhand-velocity">
                  Overhand Velocity (mph)
                </label>
                <TextInput
                  id="metrics-overhand-velocity"
                  type="number"
                  value={overhandVelocity}
                  onChange={(e) => { setOverhandVelocity(e.target.value); setHasChanges(true); }}
                  placeholder="85"
                  min={RANGE_VELOCITY_MPH.min}
                  max={RANGE_VELOCITY_MPH.max}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.overhandVelocity && <span style={errorTextStyle}>{fieldErrors.overhandVelocity}</span>}
              </div>
            </div>
          </div>

          {/* Pitching Metrics Section - Only shown for pitchers */}
          {isPitcher && (
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: '2px solid #F9550D',
              }}>
                Pitching Metrics
              </h3>
              
              <p style={{ 
                fontSize: '14px', 
                color: '#6B7280', 
                marginBottom: '16px' 
              }}>
                Add velocity and spin rate for each pitch type you throw.
              </p>
              
              {pitchEntries.map((entry, index) => (
                <React.Fragment key={entry.id}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr auto',
                  gap: '12px',
                  marginBottom: '12px',
                  alignItems: 'end'
                }}>
                  {/* Pitch Type */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px'
                    }} htmlFor={`pitch-${entry.id}-type`}>
                      Pitch Type
                    </label>
                    {/* The wrapper and the inline SVG went with the native
                        select: <Select> draws the react-feather chevron the
                        rest of the product uses, in the same place. */}
                    <Select
                      id={`pitch-${entry.id}-type`}
                      value={entry.pitch_type}
                      onChange={(e) => updatePitchEntry(entry.id, 'pitch_type', e.target.value)}
                      style={{
                        padding: '12px 36px 12px 14px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '15px',
                      }}
                    >
                      <option value="">Select...</option>
                      {availablePitchTypes.map(pt => (
                        <option key={pt} value={pt}>{pt}</option>
                      ))}
                    </Select>
                  </div>
                  
                  {/* Velocity */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px'
                    }} htmlFor={`pitch-${entry.id}-velocity`}>
                      Velocity (mph)
                    </label>
                    <TextInput
                      id={`pitch-${entry.id}-velocity`}
                      type="number"
                      value={entry.velocity}
                      onChange={(e) => updatePitchEntry(entry.id, 'velocity', e.target.value)}
                      placeholder="85"
                      min={RANGE_VELOCITY_MPH.min}
                      max={RANGE_VELOCITY_MPH.max}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontFamily: 'Manrope, sans-serif',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  
                  {/* Spin Rate */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px'
                    }} htmlFor={`pitch-${entry.id}-spin-rate`}>
                      Spin Rate (rpm)
                    </label>
                    <TextInput
                      id={`pitch-${entry.id}-spin-rate`}
                      type="number"
                      value={entry.spin_rate}
                      onChange={(e) => updatePitchEntry(entry.id, 'spin_rate', e.target.value)}
                      placeholder="2400"
                      min={RANGE_SPIN_RATE_RPM.min}
                      max={RANGE_SPIN_RATE_RPM.max}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontFamily: 'Manrope, sans-serif',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  
                  {/* Remove Button */}
                  <button
                    type="button"
                    aria-label="Remove pitch"
                    onClick={() => removePitchEntry(entry.id)}
                    style={{
                      width: '46px',
                      height: '46px',
                      padding: '0',
                      background: '#FEE2E2',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#DC2626',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
                {(pitchErrors[index]?.velocity || pitchErrors[index]?.spin_rate) && (
                  <div style={{ marginTop: '-6px', marginBottom: '12px', fontSize: '13px', color: '#DC2626' }}>
                    {pitchErrors[index]?.velocity || pitchErrors[index]?.spin_rate}
                  </div>
                )}
                </React.Fragment>
              ))}
              
              <button
                type="button"
                onClick={addPitchEntry}
                style={{
                  padding: '12px 20px',
                  background: '#FFF7ED',
                  border: '2px dashed #F9550D',
                  borderRadius: '8px',
                  color: '#F9550D',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  width: '100%',
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <Plus size={16} />
                Add Pitch Type
              </button>
            </div>
          )}
          </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '20px 28px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={handleCancel}
            disabled={isSaving}
            style={{
              padding: '12px 24px',
              fontSize: '15px',
              fontWeight: '600',
              color: '#374151',
              background: 'white',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              fontFamily: 'Manrope, sans-serif',
              transition: 'all 0.2s ease',
              opacity: isSaving ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.currentTarget.style.background = '#F9FAFB';
                e.currentTarget.style.borderColor = '#9CA3AF';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving) {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = '#D1D5DB';
              }
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || loadFailed || hasErrors}
            style={{
              padding: '12px 28px',
              fontSize: '15px',
              fontWeight: '600',
              color: 'white',
              background: (isSaving || loadFailed || hasErrors) ? '#9CA3AF' : '#F9550D',
              border: 'none',
              borderRadius: '8px',
              cursor: (isSaving || loadFailed || hasErrors) ? 'not-allowed' : 'pointer',
              fontFamily: 'Manrope, sans-serif',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.currentTarget.style.background = '#E04A0A';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 85, 13, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving) {
                e.currentTarget.style.background = '#F9550D';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
    </Modal>
  );
};

export default PerformanceMetricsEditModal;
